import { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useMsal } from '@azure/msal-react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAPICall, useAppDispatch } from '../hooks';
import { clearLogin, fetchLoginUser } from '../sdk/store/authActions';
import {
  LoggerService,
  isTokenValid,
  getLoginToken,
  getEnvironment,
  getAmwayLoginUrl,
  getAmwayLogoutUrl,
  getIdToken,
  getAccessToken,
  redirectByRole,
  isUserAuthenticated,
  isLoginAttempt,
  getState,
  updateState,
  //getAppToken,
} from '../ui/utils';
import {
  LoginTokenModel,
  IdTokenModel,
  AccountType,
  DynamicObjectInterface,
  JoinTeamTokenModel,
  AppTokenModel,
} from '../sdk/type';
import authService from '../sdk/services/authService';
import SecurityConcernService from '../sdk/services/securityConcernService';
import { InvalidType, SecurityPrivacy, SystemSupportModal } from '../ui/app-components';
import { Loading, Theme, FrameworkRole } from '../ui/components';
import AccountSelection from '../ui/app-components/AccountSelection';
import { useAdminToast } from '../ui/components/AdminToast/AdminToast';
import { PreLoginPage } from '../pages/preLoginPage/PreLoginPage';
import GameRouter from './game/GameRouter';
import { Home } from './home/Home';

export interface LandingProps {
  redirect?: boolean;
  setIsLogin: (isTrue: boolean) => void;
}

const VALID_AFF = '100';
const VALID_COUNTRIES = ['MY', 'SG'];

export const Landing = (props: LandingProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const apiCall = useAPICall();
  const { adminToast } = useAdminToast();
  const [useOutlet, setUseOutlet] = useState(false);
  const [login, setLogin] = useState(false);
  const [invalidType, setInvalidType] = useState<InvalidType>(null);
  const [showConcernModal, setShowConcernModal] = useState(false);
  const [agree, setAgree] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountType>();
  const [redirectAccount, setRedirectAccount] = useState<AccountType>();
  const [accounts, setAccounts] = useState<AccountType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImpersonate, setIsImpersonate] = useState(false);
  const { instance } = useMsal();
  const [searchParams] = useSearchParams();
  const sourceParam = searchParams.get('source');
  const teamParam = searchParams.get('t');
  const postLoginUrl = location.pathname;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const isPromotion= postLoginUrl === '/promotions';

  let isLocal = false;
  if (['LOCAL'].includes(getEnvironment())) {
    isLocal = true;
  }

  useEffect(() => {
    // is ABO login from Prod and Uat
     if (isLocal && isUserAuthenticated()){
      checkRedirect();
    } else if (!isLoginAttempt() && !isLocal && !isImpersonate) {
      setIsAuthenticated(isLoginAttempt());
    } else {
      // login attempt found - code from AmwayID & loginToken from Entra
      checkRedirect();
    }
  }, [isImpersonate, isAuthenticated]);

  useEffect(() => {
    if (!login) return;

    const init = async () => {
      // Get initial game state
      const id = await getState('id');

      // Fetch agreement only if not a game
      if (!id) {
        const agreementResponse = await apiCall(SecurityConcernService.checkAgreement);
        if (agreementResponse.status === 200) {
          const isAck = agreementResponse.data?.isAcknowledged === 1;
          setShowConcernModal(!isAck);
          setAgree(isAck);
          setIsAcknowledged(isAck);
        }
      }else{
        checkRedirect();
      }
    };

    init();
  }, [login]);

  useEffect(() => {
    if ((agree || redirectAccount) && selectedAccount) {
      checkRedirect();
    }
  }, [agree, redirectAccount, selectedAccount]);

  useEffect(() => {
    if (selectedAccount) {
      const fetchRefreshToken = async () => {
        await getRefreshToken(selectedAccount);
        setLogin(true);
      };
      fetchRefreshToken();
    }
  }, [selectedAccount]);

  const logoutFn = (doLogin: boolean = true): Function => {
    const localSignIn = () => {
      setTimeout(() => {
        // let loginUrl = '/auth/login';
        let loginUrl = '/';
        if (sourceParam && teamParam) loginUrl += `?source=${sourceParam}&t=${teamParam}`;
        if (postLoginUrl) loginUrl = postLoginUrl;
        window.location.replace(loginUrl);
      }, 0);
    };

    const entraSignOut = async () => {
      LoggerService.log('Microsoft Entra signing out');
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + '/admin',
      });
    };

    const amwayIdSignOut = () => {
      LoggerService.log('AmwayId signing out');
      window.location.href = doLogin
        ? getAmwayLoginUrl({ source: sourceParam, teamParam: teamParam, postLoginUrl: postLoginUrl })
        : getAmwayLogoutUrl();
    };

    if (isLocal) return localSignIn;

    const atoken = getLoginToken() || '';
    let decoded: LoginTokenModel | null = atoken ? jwtDecode(atoken) : null;

    const allowedIssuerHosts = [process.env.REACT_APP_AUTHORIZE_BASE_URL || 'login.microsoftonline.com'];
    let isMicrosoftIssuer = false;
    try {
      const issuer = decoded?.iss;
      if (issuer) {
        const issuerUrl = new URL(issuer);
        isMicrosoftIssuer = allowedIssuerHosts.includes(issuerUrl.hostname);
      }
    } catch {
      isMicrosoftIssuer = false;
    }

    if (isMicrosoftIssuer) {
      return entraSignOut;
    } else {
      return amwayIdSignOut;
    }
  };

  const getAccountSelectedLoggedIn = async (account?: AccountType): Promise<void> => {
    await setSelectedAccount(account);
    //Get Application Access Token
  };

  const handleAgreeConcern = () => {
    setShowConcernModal(false);
    setAgree(true);
  };

  // const handleConfirmAccount = (account: AccountDataModel) => {
  //   setSelectedUser(account);
  // };

  const checkRedirect = async () => {
    LoggerService.log("checkRedirect");
    if (typeof window !== 'undefined') {
      let loginToken = getLoginToken() || '';
      let idToken = getIdToken() || '';
      let atoken = getAccessToken() || '';
      let state: DynamicObjectInterface = localStorage.getItem('state') || '';
      state = state ? JSON.parse(atob(state)) : {};
      //LoggerService.log('State', state);
      setIsImpersonate(state.fromImpersonate || false);
      // localStorage.removeItem('reroute');

      const code = localStorage.getItem('code');

      if (code) {
        localStorage.removeItem('code');
        setIsLoading(true);
        const res = await apiCall(authService.getRequestToken, { code });
        if (res.status !== 200) {
          adminToast({
            message: 'Error occur. Please contact administrator.',
            theme: Theme.ERROR,
          });
        } else {
          loginToken = res?.data?.access_token;
          idToken = res?.data?.id_token;
          localStorage.setItem('loginToken', loginToken);
          localStorage.setItem('idToken', idToken);
          await updateState(['rt', res?.data?.refresh_token]);
        }
        //localStorage.setItem('isFirstTimeLogin', 'true');
        setIsLoading(false);
      }

      if (!isLocal && !isImpersonate) {
        if (loginToken && isTokenValid(loginToken)) {
          setIsAuthenticated(isUserAuthenticated());
          const decoded: LoginTokenModel = jwtDecode(loginToken);
          const decodedIdToken: IdTokenModel = jwtDecode(idToken);
          localStorage.setItem('globalPartyID', decoded.gbl_partyid);

          if (atoken === null || atoken === '') {
            const accounts =
              decodedIdToken.accounts?.filter(
                acc =>
                  VALID_COUNTRIES.includes(acc.country) &&
                  acc.sales_plan_aff === VALID_AFF &&
                  acc.role === 'BusinessOwner' &&
                  acc.status?.toUpperCase() === 'ACTIVE',
              ) || [];

            if (accounts.length === 0) {
              const hasValidCountry =
                (decodedIdToken.accounts?.some(acc => VALID_COUNTRIES.includes(acc.country)) &&
                  decodedIdToken.accounts?.some(acc => acc.role === 'BusinessOwner')) ||
                false;
              const hasValidSalesPlan = decodedIdToken.accounts?.some(acc => acc.sales_plan_aff === VALID_AFF) || false;
              const hasValidStatus =
                decodedIdToken.accounts?.some(acc => acc.status?.toUpperCase() === 'ACTIVE') || false;

              if (!hasValidCountry) {
                setInvalidType('invalidAff');
                return;
              }

              if (!hasValidSalesPlan) {
                setInvalidType('invalidSalesPlan');
                return;
              }

              if (!hasValidStatus) {
                setInvalidType('invalidStatus');
                return;
              }

              return;
            }
            setAccounts(accounts);

            if (accounts.length === 1 && !login) {
              setSelectedAccount(accounts[0]);
            }

            if ((agree || state.id ) && selectedAccount) {
              LoggerService.log("Account selected..");

              const body = {
                aboId: selectedAccount.abo,
                email: selectedAccount.email,
                partyId: selectedAccount.lcl_partyid,
              };

              const result = await apiCall(authService.getAppToken, body);

              if (!result?.data?.token) {
                setInvalidType('invalidToken');
                return;
              }       

              setLogin(true);
              await dispatch(fetchLoginUser(result.data?.token));

                // await updateUserPartyId({
                //   aboId: selectedAccount?.abo || '',
                //   name: partyName || '',
                //   partyId: selectedAccount?.lcl_partyid || '',
                // });

                let state: DynamicObjectInterface = localStorage.getItem('state') || '';
                state = state ? JSON.parse(atob(state)) : {};
                //LoggerService.log('State', state);

                if (agree && !isAcknowledged) {
                  await apiCall(SecurityConcernService.updateAgreement);
                }

                setIsImpersonate(state.fromImpersonate || false);
                setRedirectAccount(selectedAccount);
            }

          }
        } else {
          LoggerService.log('Landing-1: redirect to login page');
          dispatch(clearLogin(logoutFn()));
        }
      } else {
        setIsAuthenticated(isUserAuthenticated());
      }

      if (atoken != null && atoken !== '') {
        if (isTokenValid(atoken)) {
          LoggerService.log('Landing: logon user, redirect to home page');

          const paths = location.pathname.substring(1).split('/');
          const isRoot = paths.filter(path => path).length === 0;

          const decoded: AppTokenModel = jwtDecode(atoken);
          let decodedTeam: JoinTeamTokenModel | null = null;
          try {
            decodedTeam = state.teamParam
              ? (atob(state.teamParam)
                  .split(',')
                  .map(s => Number(s)) as JoinTeamTokenModel)
              : teamParam
                ? (atob(teamParam)
                    .split(',')
                    .map(s => Number(s)) as JoinTeamTokenModel)
                : null;
          } catch (error) {
            LoggerService.log('Error parsing JSON:', error);
          }

          if (decoded.frameworkRole === 'ADMIN' && paths[0] !== 'admin') navigate('/admin');
          if (decoded.frameworkRole === 'MEMBER') {
            setUseOutlet(true);

            if (props.redirect || isRoot || isPromotion) {
              const segments = postLoginUrl.substring(1).split('/').filter(Boolean);
              redirectByRole({
                dispatch,
                apiCall,
                navigate,
                source: state.source ?? sourceParam,
                id: state.id,
                postLoginUrl: state.postLoginUrl ?? (segments.length ? segments : '/'),
                token: atoken,
                userInfo: decoded as AppTokenModel,
                decodedTeam,
                setIsLogin: props.setIsLogin,
              });
            }
          }
        } else {
          LoggerService.log('Landing-2: redirect to login page');
          dispatch(clearLogin(logoutFn()));
        }
      }
    }
  };

  const getRefreshToken = async (account?: AccountType): Promise<void> => {
    const aboId = account?.abo;
    const affCode = account?.sales_plan_aff;
    const refreshToken = await getState('rt');

    const res = await apiCall(authService.getRefreshToken, { refreshToken, aboId, affCode });
    if (res.status !== 200) {
      LoggerService.log('Error occur. Please contact administrator.', { variant: 'error' });
    } else {
      const result = res?.data ?? {};
      // Set new accessToken
      localStorage.setItem('loginToken', result.access_token);
      localStorage.setItem('idToken', result.id_token);
      await updateState(['rt', result.refresh_token]);
    }

    // window.location.href = '/';
  };

  // const updateUserPartyId = async ({ aboId, name, partyId }: { aboId: string; name: string; partyId: string }) => {
  //   try {
  //     const payload = {
  //       aboId: aboId,
  //       name: name,
  //       partyId: partyId,
  //     };

  //     const res = await apiCall(userService.updateUserPartyId, payload);
  //     if (res.status === 200) {
  //       return true;
  //     } else {
  //       return false;
  //     }
  //   } catch (error) {
  //     LoggerService.log('Error occurred. Please contact administrator.', { variant: 'error' });
  //     return false;
  //   }
  // };

  if (isLoading) return <Loading />;
  if (accounts.length > 1 && !selectedAccount)
    return <AccountSelection accounts={accounts} login={getAccountSelectedLoggedIn} />;
  
  if (!isAuthenticated) {
    return <PreLoginPage accessType={FrameworkRole.MEMBER} isGame={isPromotion} />;
  }

  if (invalidType)
    return <SystemSupportModal invalidType={invalidType} onExit={() => dispatch(clearLogin(logoutFn(false)))} />;

  if (!isPromotion && showConcernModal)
    return (
      <SecurityPrivacy onAgree={() => handleAgreeConcern()} onCancel={() => dispatch(clearLogin(logoutFn(false)))} />
    );

  if (isPromotion && useOutlet) return <GameRouter />;

  return useOutlet ? <Home /> : null;
};
